import { createSlice } from '@reduxjs/toolkit';
import type { ProcessosArbitraisState } from '../types/processosArbitraisStoreTypes';
import {
  fetchDashboardProcessosArbitrais,
  fetchProcessosArbitrais,
  fetchProcessosArbitraisSituacaoOptions,
  reprocessarInicioProcessoArbitral,
  startProcessosArbitraisExport,
} from './actions';

const initialState: ProcessosArbitraisState = {
  items: [],
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1,
  },
  summary: {
    total: 0,
    pendingAction: 0,
    awaitingPayment: 0,
    concluded: 0,
  },
  isLoading: false,
  error: null,
  isExporting: false,
  situacaoOptions: [],
  reprocessandoInicioIds: [],
  dashboard: { data: null, recorte: null, isLoading: false, error: null, requestId: null },
};

const arbitralSlice = createSlice({
  name: 'arbitral',
  initialState,
  reducers: {
    setArbitralExportFinished: (state) => {
      state.isExporting = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProcessosArbitrais.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchProcessosArbitrais.fulfilled, (state, action) => {
        state.isLoading = false;
        state.items = action.payload.items;
        state.pagination = action.payload.pagination;
        state.summary = action.payload.summary;
      })
      .addCase(fetchProcessosArbitrais.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Erro ao carregar processos arbitrais';
      })
      // Só a resposta do pedido mais recente entra: a de um recorte anterior que chegar depois
      // mostraria números de outro filtro como se fossem do atual.
      .addCase(fetchDashboardProcessosArbitrais.pending, (state, action) => {
        state.dashboard.isLoading = true;
        state.dashboard.error = null;
        state.dashboard.requestId = action.meta.requestId;
      })
      .addCase(fetchDashboardProcessosArbitrais.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.dashboard.requestId) return;
        state.dashboard.isLoading = false;
        state.dashboard.data = action.payload;
        state.dashboard.recorte = action.meta.arg;
      })
      .addCase(fetchDashboardProcessosArbitrais.rejected, (state, action) => {
        if (action.meta.aborted || action.meta.requestId !== state.dashboard.requestId) return;
        state.dashboard.isLoading = false;
        state.dashboard.error = action.payload ?? 'Não foi possível carregar o dashboard';
      })
      .addCase(fetchProcessosArbitraisSituacaoOptions.fulfilled, (state, action) => {
        state.situacaoOptions = action.payload;
      })
      .addCase(startProcessosArbitraisExport.pending, (state) => {
        state.isExporting = true;
      })
      .addCase(startProcessosArbitraisExport.rejected, (state) => {
        state.isExporting = false;
      })
      .addCase(reprocessarInicioProcessoArbitral.pending, (state, action) => {
        if (!state.reprocessandoInicioIds.includes(action.meta.arg)) {
          state.reprocessandoInicioIds.push(action.meta.arg);
        }
      })
      .addCase(reprocessarInicioProcessoArbitral.fulfilled, (state, action) => {
        state.reprocessandoInicioIds = state.reprocessandoInicioIds.filter(
          (id) => id !== action.meta.arg,
        );
      })
      .addCase(reprocessarInicioProcessoArbitral.rejected, (state, action) => {
        state.reprocessandoInicioIds = state.reprocessandoInicioIds.filter(
          (id) => id !== action.meta.arg,
        );
      });
  },
});

export const { setArbitralExportFinished } = arbitralSlice.actions;

export default arbitralSlice.reducer;
